import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { SlidersHorizontal } from "lucide-react";
import { ui, cn } from "@patient-watch/ui";
import { APP_LIST_TOOLBAR_ICON_CLASS } from "@/components/common/AppListToolbar";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { useActionCopy } from "@/features/i18n/actionCopy";
import { formatRoleLabel, useProfileCopy } from "@/features/i18n/profileCopy";
import type { OptionT } from "@patient-watch/types";
import type { UserOrganisation } from "../api/selectTeamMembers";

export interface TeamFilters {
    roles: string[];
    organisations: string[];
    favouritesOnly: boolean;
}

interface TeamFilterForm {
    roles: string[];
    organisations: string[];
    favouritesOnly: boolean;
}

interface Props {
    filters: TeamFilters;
    organisations: UserOrganisation[];
    onApply: (filters: TeamFilters) => void;
}

const STAFF_ROLES = ["CLINICIAN", "SECRETARY", "MANAGER", "ADMIN", "VALIDATOR"] as const;

export function TeamFilterDialog({ filters, organisations, onApply }: Props) {
    const copy = useProfileCopy();
    const actionCopy = useActionCopy();
    const [open, setOpen] = useState(false);

    const form = useForm<TeamFilterForm>({
        defaultValues: filters,
    });

    useEffect(() => {
        if (open) {
            form.reset(filters);
        }
    }, [filters, form, open]);

    const roleOptions = useMemo(
        () =>
            STAFF_ROLES.map((role) => ({
                value: role,
                label: formatRoleLabel(role, copy),
            })) as OptionT[],
        [copy]
    );

    const organisationOptions = useMemo(
        () =>
            organisations.map((org) => ({
                value: org.organisation_id,
                label: org.organisation_name,
            })) as OptionT[],
        [organisations]
    );

    const activeFilterCount = filters.roles.length + filters.organisations.length + (filters.favouritesOnly ? 1 : 0);

    const onSubmit = (data: TeamFilterForm) => {
        onApply({
            roles: data.roles ?? [],
            organisations: data.organisations ?? [],
            favouritesOnly: Boolean(data.favouritesOnly),
        });
        setOpen(false);
    };

    return (
        <ui.Dialog open={open} onOpenChange={setOpen}>
            <ui.DialogTrigger asChild>
                <ui.Button
                    variant="outline"
                    size="icon"
                    type="button"
                    className={cn("relative md:px-3", APP_LIST_TOOLBAR_ICON_CLASS, "md:!w-fit")}
                    aria-label={copy.filterTeamAria}
                    data-testid="team-filters"
                >
                    <SlidersHorizontal className="w-4 h-4 shrink-0" />
                    <span className="hidden md:inline">{actionCopy.filters}</span>
                    {activeFilterCount > 0 && (
                        <span className="top-0 right-0 md:static absolute flex justify-center items-center bg-indigo-600 md:bg-indigo-100 md:ml-1 px-1 rounded-full min-w-[16px] md:min-w-[18px] h-4 md:h-[18px] font-medium text-2xs text-white md:text-indigo-700 leading-none">
                            {activeFilterCount}
                        </span>
                    )}
                </ui.Button>
            </ui.DialogTrigger>

            <ui.DialogContent className="sm:max-w-md">
                <ui.Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className={cn("flex flex-col space-y-3")}>
                        <ui.DialogHeader>
                            <ui.DialogTitle>{actionCopy.filters}</ui.DialogTitle>
                            <ui.DialogDescription>{copy.teamFilterDescription}</ui.DialogDescription>
                        </ui.DialogHeader>

                        <div className="space-y-1">
                            <ui.Label className="text-xs">{copy.filterRole}</ui.Label>
                            <fc.MultiSelect name="roles" options={roleOptions} trigger={copy.selectRoles} />
                        </div>
                        <div className="space-y-1">
                            <ui.Label className="text-xs">{copy.filterOrganisation}</ui.Label>
                            <fc.MultiSelect
                                name="organisations"
                                options={organisationOptions}
                                trigger={copy.selectOrganisations}
                            />
                        </div>
                        <label className="flex items-center gap-2 pt-1 text-sm">
                            <ui.Checkbox
                                checked={form.watch("favouritesOnly")}
                                onCheckedChange={(checked) => form.setValue("favouritesOnly", checked === true)}
                            />
                            {copy.filterFavouritesOnly}
                        </label>

                        <ui.DialogFooter className="gap-2 sm:gap-0">
                            <ui.DialogClose asChild>
                                <ui.Button type="button" variant="outline" size="sm">
                                    {actionCopy.cancel}
                                </ui.Button>
                            </ui.DialogClose>
                            <ui.Button type="submit" size="sm">
                                {actionCopy.save}
                            </ui.Button>
                        </ui.DialogFooter>
                    </form>
                </ui.Form>
            </ui.DialogContent>
        </ui.Dialog>
    );
}
